import { useState, useEffect } from "react";

const Selector = ({ tabsData, clickHandler, active }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(active);
  
  useEffect(() => {
    setActiveTabIndex(active)
  }, [active]);

  const getVisibleTabs = () => {
    const tabs = [];
    
    const isMobile = window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 5;
    
    tabs.push(activeTabIndex);
    
    for (let i = 1; i <= Math.floor(maxVisible / 2); i++) {
      if (activeTabIndex - i >= 0 && tabs.length < maxVisible) {
        tabs.unshift(activeTabIndex - i);
      }
    }
    
    for (let i = 1; i <= Math.floor(maxVisible / 2); i++) {
      if (activeTabIndex + i < tabsData.length && tabs.length < maxVisible) {
        tabs.push(activeTabIndex + i);
      }
    }
    
    while (tabs[0] > 0 && tabs.length < maxVisible) {
      tabs.unshift(tabs[0] - 1);
    }
    
    while (tabs[tabs.length - 1] < tabsData.length - 1 && tabs.length < maxVisible) {
      tabs.push(tabs[tabs.length - 1] + 1);
    }
    
    return tabs.sort((a, b) => a - b);
  };

  const visibleTabs = getVisibleTabs();
  const showFirst = !visibleTabs.includes(0) && activeTabIndex > 1;
  const showLast = !visibleTabs.includes(tabsData.length - 1) && activeTabIndex < tabsData.length - 2;
  const showPrev = activeTabIndex > 0;
  const showNext = activeTabIndex < tabsData.length - 1;

  return (
    <div className="flex items-center justify-center space-x-1 bg-white/20 backdrop-blur-sm rounded-xl p-1 sm:p-2 border border-white/30 max-w-full overflow-hidden">
      {/* First Button */}
      {showFirst && (
        <button
          className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
          onClick={() => {
            clickHandler(0);
            setActiveTabIndex(0);
          }}
          title="First part"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Previous Button */}
      {showPrev && (
        <button
          className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
          onClick={() => {
            const prev = Math.max(0, activeTabIndex - 1);
            clickHandler(prev);
            setActiveTabIndex(prev);
          }}
          title="Previous part"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Ellipsis before current if needed */}
      {visibleTabs[0] > 0 && (
        <span className="text-white/70 px-1 sm:px-2 text-xs">...</span>
      )}

      {/* Visible Tabs */}
      <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide">
        {visibleTabs.map((idx) => (
          <button
            key={idx}
            className={`min-w-[40px] sm:min-w-[60px] px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm flex-shrink-0 border
                        ${
                          idx === activeTabIndex
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg border-blue-400/30 font-semibold"
                          : "bg-white/60 text-gray-700 hover:text-gray-900 hover:bg-white/80 border-white/40 hover:border-white/60 hover:shadow-md"
                        }
                      `}
            onClick={() => {
              clickHandler(idx);
              setActiveTabIndex(idx);
            }}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {/* Ellipsis after current if needed */}
      {visibleTabs[visibleTabs.length - 1] < tabsData.length - 1 && (
        <span className="text-white/70 px-1 sm:px-2 text-xs">...</span>
      )}

      {/* Next Button */}
      {showNext && (
        <button
          className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
          onClick={() => {
            const next = Math.min(tabsData.length - 1, activeTabIndex + 1);
            clickHandler(next);
            setActiveTabIndex(next);
          }}
          title="Next part"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Last Button */}
      {showLast && (
        <button
          className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
          onClick={() => {
            clickHandler(tabsData.length - 1);
            setActiveTabIndex(tabsData.length - 1);
          }}
          title="Last part"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default Selector;